
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'environment';
import { of as observableOf } from 'rxjs';
import { catchError, finalize, map, publishReplay, refCount, take } from 'rxjs/operators';
import { ApiError, LoginApi, NedermanUserApi, NedermanUserRequest } from '../backend/api';
import { Rest } from '../backend/rest';
import { ErrorComponent } from '../shared/error/error.component';
import * as IntroJs from 'intro.js';

const intro = IntroJs.introJs();

@Component({
  selector: 'app-root',
  templateUrl: './root.component.html',
  styleUrls: ['./root.component.scss']
})
export class RootComponent implements OnInit {
  @ViewChild('error')
  public errorComponent: ErrorComponent;

  public environment = environment;
  public nordfab: boolean = environment.nordfab();

  public currentUser: string;
  public userOptions = false;
  public roleId: string;
  public roleName: string;
  public previousLogin: string;
  public roleGuideStep = false;
  public showApplications = false;

  public tourRoutes = [
    /^\/nederman\/companies\/.+/i,
    /^\/nederman\/users\/.+/i,
    /^\/customer\/companies\/.+/i,
    /^\/customer\/users\/.+/i,
    /^\/customer\/accounts\/.+/i,
    /^\/applications\/.+/i
  ];
  public tourShown: string;

  public query: NedermanUserRequest = {
    objectId: ''
  };
  public grants: any;
  public selectedRole: string;

  constructor(
    private router: Router,
    private backend: Rest,
    private nedermanUserApi: NedermanUserApi,
    private loginService: LoginApi
  ) { }

  ngOnInit() {
    this.setIntroJs();

    this.currentUser = localStorage.getItem('displayName');
    this.query.objectId = localStorage.getItem('objectId');
    this.roleId = localStorage.getItem('roleId');

    this.previousLogin = localStorage.getItem('previousLogin');
    this.tourShown = localStorage.getItem('tourShown');

    this.showApplications = this.roleId === 'HoldingAdmin' ? true : false;

    if (this.roleId === 'HoldingAdmin' || this.roleId === 'NedermanAdmin') {
      this.grants = this.loginService.grants().pipe(
        map(res => {
          if (res.grants.find(grant => grant.selected)) {
            this.selectedRole = res.grants.find(grant => grant.selected).grantId;
          } else {
            this.selectedRole = res.grants[0].grantId;
            this.errorComponent.show('Your role has been revoked.', 501);
            this.errorComponent.onHide.subscribe((reason: string) => {
              this.reissue();
            });
          }
          res.grants.length <= 1 ? this.roleGuideStep = false : this.roleGuideStep = true;
          return res.grants;
        }), finalize(() => {
          // GIVE ANGULAR TIME TO SHOW ROLE ELEMENT
          setTimeout(() => {
            if (!this.previousLogin && !this.tourShown) {
              this.startInitialTour();
            }
          });
        }), publishReplay(1), refCount(), catchError((err: ApiError) => {
          this.errorComponent.show(err.message, err.code);
          return observableOf(false);
        }));
    } else {
      if (!this.previousLogin && !this.tourShown) {
        setTimeout(() => {
          intro.start();
          localStorage.setItem('tourShown', 'true');
        });
      }
    }
  }

  selectRole() {
    this.reissue();
  }

  logout() {
    // to do discard session
    localStorage.clear();
    window.location.href = 'https://login.windows.net/17c507de-7563-45c5-a1bc-4767cd25c217/oauth2/logout';
  }

  reissue() {
    this.loginService.reissue(this.selectedRole).pipe(take(1)).subscribe(session => {
      this.backend.setSessionToken(session.sessionId, session.token);
      localStorage.setItem('roleId', session.role);
      location.reload();
    }, (err: ApiError) => {
      this.errorComponent.show(err.message, err.code);
    });
  }

  startTour() {
    let initialTour = true;
    this.tourRoutes.forEach(reg => {
      if (this.router.url.match(reg)) {
        initialTour = false;
      }
    });
    initialTour ? this.startInitialTour() : intro.start();
  }

  setIntroJs() {
    intro.onchange(targetElement => {
      if (targetElement) {
        targetElement.scrollIntoView(false);
      }
    });
    intro.setOptions(
      {
        nextLabel: 'Next',
        showBullets: false,
        showStepNumbers: false,
        overlayOpacity: 0.4,
        tooltipClass: 'custom-tooltip',
        highlightClass: 'custom-tooltip-helper',
        hidePrev: true,
        hideNext: true,
        showSkipButton: false
      }
    );
  }

  startInitialTour() {
    intro.onexit(() => {
      localStorage.setItem('tourShown', 'true');
      intro.setOptions({
        steps: []
      });
    });

    const initialSteps = [
      {
        element: '#initialStep3',
        intro: `
        <div class="tooltip-title">
          Customer
        </div>
        <div class="tooltip-content">
          View, search, and modify all Customer companies, users and ERP customer accounts.
        </div>
        `
      },
      {
        element: '#initialStep5',
        intro: `
        <div class="tooltip-title">
          Audit
        </div>
        <div class="tooltip-content">
          View audit logs of user actions.
        </div>
        `
      }
    ];
    if (this.roleId === 'HoldingAdmin' || this.roleId === 'NedermanAdmin') {
      initialSteps.unshift({
        element: '#initialStep2',
        intro: `
          <div class="tooltip-title">
            Nederman
          </div>
          <div class="tooltip-content">
            View, search, and modify all Nederman companies and users.
          </div>
          `
      });
    }
    if (this.showApplications) {
      initialSteps.splice(2, 0,
        {
          element: '#initialStep4',
          intro: `
          <div class="tooltip-title">
            Applications
          </div>
          <div class="tooltip-content">
            View all applications including companies and users which are activated for a specific application.
          </div>
          `
        });
    }
    if (this.roleGuideStep) {
      initialSteps.unshift(
        {
          element: '#initialStep1',
          intro: `
            <div class="tooltip-title">
              Role
            </div>
            <div class="tooltip-content">
              Switch between application roles to work within a specific company scope.
            </div>
          `
        }
      );
    }
    intro.setOptions({
      steps: initialSteps
    });
    intro.start();
  }
}
